import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { clientApi } from '../lib/api';
import { formatPrice } from '../lib/utils';
import { Calendar, ArrowLeft, Plus, Pause, Play, Clock, CheckCircle, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import Logo from '../components/Logo';

const DAY_NAMES = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const SLOT_TYPES = [
  { value: 'matin', label: 'Matin (8h-12h)' },
  { value: 'apres_midi', label: 'Après-midi (13h30-17h30)' }
];

export default function RecurringSlotPage() {
  const { user } = useAuth();
  const { config } = useConfig();
  const [eligibility, setEligibility] = useState(null);
  const [recurringSlots, setRecurringSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [processing, setProcessing] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    day_of_week: 1, // Mardi par défaut
    slot_type: 'matin',
    preferred_time: '',
    frequency_weeks: 1,
    postal_code: '',
    household_size: 4,
    formula: 'Classique',
    options: {
      lunch: false,
      drive: false,
      dessert: false,
      patisserie: false
    }
  });
  
  // Options
  const [pricing, setPricing] = useState({ formulas: [], options: [] });
  const [menus, setMenus] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [eligRes, slotsRes, pricingRes, menusRes] = await Promise.all([
        clientApi.getRecurringEligibility(),
        clientApi.getRecurringSlots(),
        clientApi.getPricing(),
        clientApi.getMenus()
      ]);
      
      setEligibility(eligRes.data);
      setRecurringSlots(slotsRes.data);
      setPricing(pricingRes.data);
      setMenus(menusRes.data);
      
      // Pre-fill postal code from user
      if (user?.postal_code) {
        setFormData(prev => ({ ...prev, postal_code: user.postal_code }));
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.postal_code) {
      toast.error('Veuillez entrer votre code postal');
      return;
    }
    
    setCreating(true);
    try {
      const response = await clientApi.createRecurringSlot(formData);
      toast.success(response.data.message);
      setShowCreateDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleDeactivate = async (recurringId) => {
    setProcessing(recurringId);
    try {
      await clientApi.deactivateRecurringSlot(recurringId);
      toast.success('Créneau désactivé');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setProcessing(null);
    }
  };

  const handleReactivate = async (recurringId) => {
    setProcessing(recurringId);
    try {
      const response = await clientApi.reactivateRecurringSlot(recurringId);
      toast.success(`Créneau réactivé ! Prochaine réservation: ${response.data.next_reservation_date}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setProcessing(null);
    }
  };

  const getFormulaPrice = () => {
    const formula = pricing.formulas?.find(
      f => f.formula_name === formData.formula && f.household_size === formData.household_size
    );
    return formula?.price || 0;
  };

  const getTotalPrice = () => {
    let total = getFormulaPrice();
    Object.entries(formData.options).forEach(([key, enabled]) => {
      if (enabled) {
        const opt = pricing.options?.find(o => o.option_key === key);
        if (opt) total += opt.price;
      }
    });
    return total;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#A5BBA8]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      {/* Header */}
      <header className="header-glass sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/client" className="flex items-center gap-2 text-[#5C6A61] hover:text-[#2A312C]">
              <ArrowLeft className="w-5 h-5" />
              <span>Retour</span>
            </Link>
            <Logo />
            <div className="w-20" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-['Cormorant_Garamond'] text-3xl font-medium text-[#2A312C] mb-2">
          Créneaux récurrents
        </h1>
        <p className="text-[#5C6A61] mb-8">
          Réservez automatiquement chaque semaine et ne manquez jamais une prestation.
        </p>

        {/* Eligibility Card */}
        <div className="card-default mb-8" data-testid="eligibility-card">
          {eligibility?.is_eligible ? (
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h2 className="font-semibold text-[#2A312C]">Vous êtes éligible !</h2>
                <p className="text-sm text-[#5C6A61]">
                  {eligibility.completed_reservations} prestations terminées. 
                  {eligibility.has_payment_method 
                    ? " Vous pouvez activer un créneau récurrent."
                    : " Ajoutez une carte de paiement pour activer."}
                </p>
              </div>
              {eligibility.has_payment_method && (
                <button
                  onClick={() => setShowCreateDialog(true)}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="create-recurring-btn"
                >
                  <Plus className="w-5 h-5" />
                  Activer un créneau
                </button>
              )}
              {!eligibility.has_payment_method && (
                <Link to="/client/payment" className="btn-outline">
                  Ajouter une carte
                </Link>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#E8B25C]/20 flex items-center justify-center">
                <Clock className="w-6 h-6 text-[#E8B25C]" />
              </div>
              <div>
                <h2 className="font-semibold text-[#2A312C]">Encore {eligibility?.remaining} prestation(s)</h2>
                <p className="text-sm text-[#5C6A61]">
                  Vous pourrez activer un créneau récurrent après {eligibility?.min_required} prestations terminées.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Active Recurring Slots */}
        <h2 className="font-semibold text-[#2A312C] mb-4">Mes créneaux récurrents</h2>
        
        {recurringSlots.length === 0 ? (
          <div className="card-default text-center py-12">
            <RefreshCw className="w-12 h-12 text-[#E3DED5] mx-auto mb-4" />
            <p className="text-[#5C6A61]">Aucun créneau récurrent actif</p>
            <p className="text-sm text-[#5C6A61] mt-2">
              Activez un créneau pour réserver automatiquement chaque semaine.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {recurringSlots.map((slot) => (
              <div 
                key={slot.recurring_id} 
                className={`card-default ${!slot.is_active ? 'opacity-60' : ''}`}
                data-testid={`recurring-slot-${slot.recurring_id}`}
              >
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-[#2A312C]">
                        {DAY_NAMES[slot.day_of_week]} - {slot.slot_type === 'matin' ? 'Matin' : 'Après-midi'}
                      </h3>
                      <Badge className={slot.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                        {slot.is_active ? 'Actif' : 'Désactivé'}
                      </Badge>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-2 text-sm text-[#5C6A61]">
                      <p>Formule: {slot.formula} • {slot.household_size} pers.</p>
                      <p>Code postal: {slot.postal_code}</p>
                      <p>Fréquence: {({1:'Toutes les semaines',2:'Toutes les 2 semaines',3:'Toutes les 3 semaines',4:'Toutes les 4 semaines'})[slot.frequency_weeks || 1]}</p>
                      {slot.next_reservation_date && slot.is_active && (
                        <p className="text-[#A5BBA8]">
                          Prochaine: {new Date(slot.next_reservation_date).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                      <p>Réservations créées: {slot.reservations_created || 0}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {slot.is_active ? (
                      <button
                        onClick={() => handleDeactivate(slot.recurring_id)}
                        disabled={processing === slot.recurring_id}
                        className="btn-outline text-sm px-4 py-2 inline-flex items-center gap-2 text-red-600 border-red-300 hover:bg-red-50"
                      >
                        {processing === slot.recurring_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Pause className="w-4 h-4" />
                        )}
                        Désactiver
                      </button>
                    ) : (
                      <button
                        onClick={() => handleReactivate(slot.recurring_id)}
                        disabled={processing === slot.recurring_id}
                        className="btn-outline text-sm px-4 py-2 inline-flex items-center gap-2 text-green-600 border-green-300 hover:bg-green-50"
                      >
                        {processing === slot.recurring_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                        Réactiver
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info Card */}
        <div className="mt-8 p-4 bg-[#A5BBA8]/10 border border-[#A5BBA8]/30 rounded-xl">
          <h3 className="font-semibold text-[#2A312C] mb-2">Comment ça marche ?</h3>
          <ul className="text-sm text-[#5C6A61] space-y-2">
            <li>• Votre créneau est automatiquement réservé chaque semaine</li>
            <li>• Le paiement est prélevé automatiquement sur votre carte</li>
            <li>• Vous pouvez désactiver à tout moment</li>
            <li>• En cas d'échec de paiement, vous avez 24h pour régulariser</li>
          </ul>
        </div>
      </main>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-xl">
              Activer un créneau récurrent
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Day of week */}
            <div>
              <Label className="text-[#2A312C] mb-2 block">Jour de la semaine</Label>
              <select
                value={formData.day_of_week}
                onChange={(e) => setFormData({ ...formData, day_of_week: parseInt(e.target.value) })}
                className="input-field"
              >
                {DAY_NAMES.map((day, idx) => (
                  <option key={idx} value={idx}>{day}</option>
                ))}
              </select>
            </div>

            {/* Fréquence */}
            <div>
              <Label className="text-[#2A312C] mb-2 block">Fréquence</Label>
              <select
                value={formData.frequency_weeks}
                onChange={(e) => setFormData({ ...formData, frequency_weeks: parseInt(e.target.value) })}
                className="input-field"
                data-testid="recurring-frequency-select"
              >
                <option value={1}>Toutes les semaines</option>
                <option value={2}>Toutes les 2 semaines (environ 2 prestations par mois)</option>
                <option value={3}>Toutes les 3 semaines</option>
                <option value={4}>Toutes les 4 semaines (environ 1 prestation par mois)</option>
              </select>
              <p className="text-xs text-[#5C6A61] mt-1">
                Choisissez à quelle fréquence vous souhaitez retrouver votre créneau habituel. Le jour et l'heure restent identiques ; seule la fréquence change.
              </p>
            </div>
            
            {/* Slot type */}
            <div>
              <Label className="text-[#2A312C] mb-2 block">Créneau horaire</Label>
              <div className="grid grid-cols-2 gap-2">
                {SLOT_TYPES.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setFormData({ ...formData, slot_type: type.value })}
                    className={`p-3 rounded-lg border-2 text-sm transition-all ${
                      formData.slot_type === type.value
                        ? 'border-[#A5BBA8] bg-[#A5BBA8]/10'
                        : 'border-[#E3DED5] hover:border-[#A5BBA8]/50'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Postal code */}
            <div>
              <Label className="text-[#2A312C] mb-2 block">Code postal</Label>
              <input
                type="text"
                value={formData.postal_code}
                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                className="input-field"
                placeholder="75001"
                maxLength={5}
              />
            </div>
            
            {/* Household size */}
            <div>
              <Label className="text-[#2A312C] mb-2 block">Taille du foyer</Label>
              <div className="grid grid-cols-6 gap-2">
                {[1, 2, 3, 4, 5, 6].map((size) => (
                  <button
                    key={size}
                    onClick={() => setFormData({ ...formData, household_size: size })}
                    className={`p-2 rounded-lg border-2 text-sm font-medium transition-all ${
                      formData.household_size === size
                        ? 'border-[#A5BBA8] bg-[#A5BBA8]/10'
                        : 'border-[#E3DED5] hover:border-[#A5BBA8]/50'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Formula */}
            <div>
              <Label className="text-[#2A312C] mb-2 block">Formule</Label>
              <select
                value={formData.formula}
                onChange={(e) => setFormData({ ...formData, formula: e.target.value })}
                className="input-field"
              >
                <option value="Classique">Classique</option>
                <option value="Végétarien">Végétarien</option>
              </select>
            </div>
            
            {/* Options */}
            <div>
              <Label className="text-[#2A312C] mb-2 block">Options</Label>
              <div className="space-y-2">
                {[
                  { key: 'lunch', label: 'Lunch Box', price: pricing.options?.find(o => o.option_key === 'lunch')?.price },
                  { key: 'dessert', label: 'Dessert', price: pricing.options?.find(o => o.option_key === 'dessert')?.price },
                  { key: 'patisserie', label: 'Pâtisserie', price: pricing.options?.find(o => o.option_key === 'patisserie')?.price },
                ].map((opt) => (
                  <label key={opt.key} className="flex items-center gap-3 p-3 bg-[#F9F6F0] rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.options[opt.key] || false}
                      onChange={(e) => setFormData({
                        ...formData,
                        options: { ...formData.options, [opt.key]: e.target.checked }
                      })}
                      className="w-4 h-4 accent-[#A5BBA8]"
                    />
                    <span className="flex-1">{opt.label}</span>
                    {opt.price && <span className="text-sm text-[#5C6A61]">+{formatPrice(opt.price)}</span>}
                  </label>
                ))}
              </div>
            </div>
            
            {/* Total */}
            <div className="p-4 bg-[#A5BBA8]/10 rounded-xl">
              <div className="flex justify-between items-center">
                <span className="font-medium text-[#2A312C]">Total par prestation</span>
                <span className="text-2xl font-semibold text-[#2A312C]">{formatPrice(getTotalPrice())}</span>
              </div>
              <p className="text-xs text-[#5C6A61] mt-1">
                Fréquence : {({1:'toutes les semaines',2:'toutes les 2 semaines',3:'toutes les 3 semaines',4:'toutes les 4 semaines'})[formData.frequency_weeks || 1]} — prélevé automatiquement à chaque prestation
              </p>
            </div>
            
            {/* Warning */}
            <div className="p-3 bg-[#E8B25C]/10 border border-[#E8B25C]/30 rounded-lg">
              <p className="text-sm text-[#5C6A61]">
                <strong>Important:</strong> Le paiement sera prélevé automatiquement sur votre carte. 
                En cas d'échec, vous aurez 24h pour régulariser avant que le créneau ne soit libéré.
              </p>
            </div>
            
            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setShowCreateDialog(false)}
                className="btn-outline flex-1"
              >
                Annuler
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !formData.postal_code}
                className="btn-primary flex-1"
              >
                {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Activer le créneau'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
